import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { useAuth } from './AuthProvider';
import UserContext from './UserContext';
import { useDealership } from './DealershipContext';

const EmployeeContext = createContext();

export const EmployeeProvider = ({ children }) => {
  const { api } = useAuth();
  const { user } = useContext(UserContext);
  const { dealership } = useDealership();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // [1] Fetch employees for a dealership. Only call when dealership changes or on demand.
  const fetchEmployees = useCallback(async (dealerBac) => {
    if (!dealerBac) return;

    setLoading(true);
    try {
      const response = await api.get(`/api/getDealerEmployees/${dealerBac}`);
      // [1a] Robust: handle array or nested field
      let list = [];
      if (Array.isArray(response.data.employee)) {
        list = response.data.employee;
      } else if (Array.isArray(response.data.employees)) {
        list = response.data.employees;
      } else if (Array.isArray(response.data)) {
        list = response.data;
      }
      setEmployees(list);
      setError(null);
    } catch (err) {
      setEmployees([]);
      setError(err.response?.data?.error || 'Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Refresh employees whenever the logged in user or selected dealership changes
  // Clearing employees on logout prevents stale data when a different user logs in
  // and ensures a fresh fetch for the new dealership/user combo
  useEffect(() => {
    if (!user) {
      setEmployees([]);
      setError(null);
      return;
    }
    if (dealership?.bac) {
      fetchEmployees(dealership.bac);
    }
  }, [user?.id, dealership?.bac, fetchEmployees]);


  // [2] Create a new employee for the current dealership
  const createEmployee = async (employeeData) => {
    console.log('Creating employee with data:', employeeData);
    const { dealershipBac } = employeeData;
    // Validate new dealershipBac field
    if (!dealershipBac) throw new Error('No dealership BAC provided');
    try {
      const response = await api.post(`/api/createEmployee`, employeeData);
      // [2a] Add the new employee to state
      setEmployees(prev =>
        Array.isArray(prev) ? [...prev, response.data.employee] : [response.data.employee]
      );
      return response.data.employee;
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.error?.includes('Email is already in use')) {
        throw new Error('Email is already in use');
      }
      throw new Error(err.response?.data?.error || 'Failed to create employee');
    }
  };

  // [3] Update employee details by employeeId
  const updateEmployee = async (employeeId, updateData) => {
    try {
      const response = await api.put(`/api/updateEmployee/${employeeId}`, updateData);
      // [3a] Update state in-place
      setEmployees(prev =>
        Array.isArray(prev)
          ? prev.map(emp => emp.id === employeeId ? response.data.employee : emp)
          : [response.data.employee]
      );
      return response.data.employee;
    } catch (err) {
      throw new Error(err.response?.data?.error || 'Failed to update employee');
    }
  };

  // [4] Archive (soft delete) an employee
  const archiveEmployee = async (employeeId) => {
    try {
      await api.put(`/api/archiveEmployee/${employeeId}`);
      // [4a] Remove employee from state
      setEmployees(prev =>
        Array.isArray(prev) ? prev.filter(emp => emp.id !== employeeId) : []
      );
    } catch (err) {
      throw new Error(err.response?.data?.error || 'Failed to archive employee');
    }
  };

  return (
    <EmployeeContext.Provider
      value={{
        employees,
        loading,
        error,
        fetchEmployees,
        createEmployee,
        updateEmployee,
        archiveEmployee,
        setError
      }}
    >
      {children}
    </EmployeeContext.Provider>
  );
};

export const useEmployees = () => {
  const context = useContext(EmployeeContext);
  if (!context) {
    throw new Error('useEmployees must be used within an EmployeeProvider');
  }
  return context;
};

export default EmployeeContext;
